import { TICK_RATE } from "../constants.js";
import { falloffMult, type WeaponStats } from "../combat/weapons.js";
import type { LimbId } from "../combat/health.js";
import type { AABB } from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import type { MapBox, MaterialId } from "../maps/types.js";
import type { CollisionWorld } from "./collision.js";
import { raycastPose, type HitPose, type LimbBox } from "./hitbox.js";

/**
 * Travelling projectiles. Bullets are simulated, not hitscanned — they take
 * time, drop and punch through what they can afford — which is also what makes
 * lag compensation honest: the server flies the round on its own ticks.
 * Deterministic and rng-FREE; spread and recoil are applied before spawn.
 */

/**
 * Gravity on a bullet, m/s². Real gravity, NOT the gameplay `GRAVITY` (18) for
 * jumps, which would put twice the real drop on every shot.
 */
export const BULLET_GRAVITY = 9.81;

/**
 * Penetration power consumed PER METRE of material, so thickness is what
 * matters and a clipped corner costs less than the middle. A 30-pen round walks
 * through ~1.2 m of wood, ~0.42 m of brick, ~0.27 m of concrete.
 */
export const MATERIAL_TOUGHNESS: Record<MaterialId, number> = {
  // A 6 cm pane costs ~0.2 pen: every round goes through, and breaks it.
  glass: 3,
  wood: 25,
  dirt: 45,
  brick: 70,
  stone: 90,
  concrete: 110,
  metal: 160,
};

/**
 * Upward elevation, radians, that zeroes the sights at `def.zeroRangeM`; the
 * client adds it to the view pitch. `stepProjectile` is symplectic Euler at the
 * fixed tick, so the drop after time `t` is (g/2)·t·(t + dt), and the dt term
 * is folded in so the zero is exact under the sim as written:
 *
 *   α = drop(Z) / Z = (g/2)·(Z/v² + dt/v)
 *
 * ~0.7 mrad for the carbine (700 m/s, 50 m zero).
 */
export function sightZeroElevationRad(def: WeaponStats): number {
  const dt = 1 / TICK_RATE;
  const v = def.muzzleVelocity;
  const z = def.zeroRangeM;
  if (!(v > 0) || !(z > 0)) return 0;
  return (BULLET_GRAVITY / 2) * (z / (v * v) + dt / v);
}

export interface ProjectileState {
  pos: Vec3;
  vel: Vec3;
  /** Damage before range falloff; reduced by every wall punched through. */
  baseDamage: number;
  penPower: number;
  /** Total metres flown so far — falloff is applied over this, not per step. */
  distance: number;
  /** Never hit its own shooter; also the attribution for the kill. */
  shooterId: string;
  // Copied off the weapon at spawn, so a projectile stays valid after the gun
  // is dropped or swapped.
  falloffStartM: number;
  falloffEndM: number;
  falloffMinMult: number;
}

/** Max walls one projectile may punch through in a SINGLE step, so a bullet
 *  fired down a corridor of thin panels cannot make one tick unbounded. */
const MAX_WALL_PENETRATIONS = 4;

/** Nudge past a wall's exit face, so the next scan cannot re-hit the same box. */
const EXIT_EPS = 1e-4;

/** Beyond this many metres a projectile is retired regardless of the world. */
export const MAX_PROJECTILE_RANGE = 600;

/** Below this horizontal speed a round has lost the plot and is retired. */
export const MIN_PROJECTILE_SPEED = 50;

/** Damage retained after punching through one wall. */
const PENETRATION_DAMAGE_MULT = 0.8;

export function spawnProjectile(
  def: WeaponStats,
  shooterId: string,
  origin: Vec3,
  dir: Vec3,
): ProjectileState {
  return {
    pos: { x: origin.x, y: origin.y, z: origin.z },
    vel: {
      x: dir.x * def.muzzleVelocity,
      y: dir.y * def.muzzleVelocity,
      z: dir.z * def.muzzleVelocity,
    },
    baseDamage: def.damage,
    penPower: def.penPower,
    distance: 0,
    shooterId,
    falloffStartM: def.falloffStartM,
    falloffEndM: def.falloffEndM,
    falloffMinMult: def.falloffMinMult,
  };
}

/** A shootable entity, as rewound to the shot's render tick by the server. */
export interface EntityTarget {
  id: string;
  pose: HitPose;
  /**
   * This entity's OWN hitboxes, entity-local. ABSENT means the human silhouette
   * (every pre-wildlife caller); PRESENT means an animal's
   * `SPECIES[...].boxes`. Passed straight through to `raycastPose` — a bullet
   * does not care what it flies at.
   */
  boxes?: readonly LimbBox[];
}

export interface EntityHit {
  targetId: string;
  limb: LimbId;
  /** Final damage, falloff already applied. Armour is resolved by the caller. */
  damage: number;
  /** World-space impact point, for effects and forensics. */
  pos: Vec3;
}

/** A pane the round passed through, and where it entered. */
export interface GlassPass {
  boxIndex: number;
  pos: Vec3;
}

export interface ProjectileStepResult {
  /** The advanced projectile, or `null` when it is finished this step. */
  state: ProjectileState | null;
  hit: EntityHit | null;
  /** Impact point when the round was stopped by geometry. */
  wallStop: Vec3 | null;
  /**
   * What the round stopped ON. NON-NULL EXACTLY WHEN `wallStop` IS; required so
   * every return site states it. A label only — the material already decided
   * penetration — for the impact sound the caller cannot recover.
   */
  wallMaterial: MaterialId | null;
  /**
   * Panes (`"glass"` solids) the round went THROUGH this step, entry points,
   * in the order it met them. `null` when there were none — the common path
   * allocates nothing.
   */
  glass: GlassPass[] | null;
}

/**
 * Advances one projectile by exactly one fixed timestep. At 700 m/s a step is
 * ~23 m, so the step is a SEGMENT and every wall and entity along it is tested,
 * nearest first. `solids` must be the hard-solid array `world` was built from
 * (non-negative `RayHit.boxIndex` values index it); terrain hits carry their
 * material directly. ENTITY first (ties go to the entity, so someone hugging a
 * wall is shootable) and the round ends in the body — one bullet, one victim.
 * WALL: thickness is the ray's exit t minus entry t through that box, so an
 * angled shot pays for its longer path.
 */
export function stepProjectile(
  p: ProjectileState,
  world: CollisionWorld,
  solids: readonly MapBox[],
  targets: readonly EntityTarget[],
  dt: number,
): ProjectileStepResult {
  // 1. Gravity, then the segment for this step.
  const vx = p.vel.x;
  const vy = p.vel.y - BULLET_GRAVITY * dt;
  const vz = p.vel.z;

  const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
  // A degenerate direction (zero, or NaN from bad input) has no segment to
  // scan and no future worth simulating — retire it quietly.
  if (!(speed > 0)) {
    return { state: null, hit: null, wallStop: null, wallMaterial: null, glass: null };
  }

  const segLen = speed * dt;
  const dirX = vx / speed;
  const dirY = vy / speed;
  const dirZ = vz / speed;
  const dir: Vec3 = { x: dirX, y: dirY, z: dirZ };

  let curX = p.pos.x;
  let curY = p.pos.y;
  let curZ = p.pos.z;
  let remaining = segLen;
  let distance = p.distance;
  let penPower = p.penPower;
  let baseDamage = p.baseDamage;
  let penetrations = 0;
  let glass: GlassPass[] | null = null;

  const cur: Vec3 = { x: curX, y: curY, z: curZ };

  while (remaining > 0) {
    cur.x = curX;
    cur.y = curY;
    cur.z = curZ;

    const wall = world.raycast(cur, dir, remaining);

    // 2. Nearest entity along the remaining segment, shooter excluded.
    let bestEntDist = Infinity;
    let bestEntLimb: LimbId | null = null;
    let bestEntId = "";
    for (let i = 0; i < targets.length; i++) {
      const target = targets[i] as EntityTarget;
      if (target.id === p.shooterId) continue;
      // `target.boxes` passed straight through, `undefined` included — no
      // allocation, no second code path.
      const r = raycastPose(cur, dir, remaining, target.pose, target.boxes);
      if (r !== null && r.dist < bestEntDist) {
        bestEntDist = r.dist;
        bestEntLimb = r.limb;
        bestEntId = target.id;
      }
    }

    // 3. Entity first (including ties with a wall).
    if (bestEntLimb !== null && (wall === null || bestEntDist <= wall.dist)) {
      const total = distance + bestEntDist;
      const mult = falloffMult(
        total,
        p.falloffStartM,
        p.falloffEndM,
        p.falloffMinMult,
      );
      return {
        state: null,
        hit: {
          targetId: bestEntId,
          limb: bestEntLimb,
          damage: baseDamage * mult,
          pos: {
            x: curX + dirX * bestEntDist,
            y: curY + dirY * bestEntDist,
            z: curZ + dirZ * bestEntDist,
          },
        },
        wallStop: null,
        wallMaterial: null,
        glass,
      };
    }

    // 4. Wall.
    if (wall !== null) {
      const solid = solids[wall.boxIndex];
      const material: MaterialId = wall.material ?? solid?.material ?? "concrete";
      const impact: Vec3 = {
        x: curX + dirX * wall.dist,
        y: curY + dirY * wall.dist,
        z: curZ + dirZ * wall.dist,
      };
      // Missing material data is treated as the toughest common surface.
      // Terrain
      // is a one-sided heightfield with earth below, not a thin wall.
      if (wall.boxIndex < 0) {
        return { state: null, hit: null, wallStop: impact, wallMaterial: material, glass };
      }
      const exitT = solid === undefined ? wall.dist : exitDistance(cur, dir, solid.box);
      const thickness = exitT > wall.dist ? exitT - wall.dist : 0;
      const penCost = MATERIAL_TOUGHNESS[material] * thickness;

      // Glass is reported, not resisted: a pane costs its (tiny) pen and
      // neither the penetration budget nor the damage multiplier.
      const isGlass = material === "glass";
      if (penPower > penCost && (isGlass || penetrations < MAX_WALL_PENETRATIONS)) {
        if (isGlass) {
          if (glass === null) glass = [];
          glass.push({ boxIndex: wall.boxIndex, pos: impact });
        } else {
          penetrations++;
          baseDamage *= PENETRATION_DAMAGE_MULT;
        }
        penPower -= penCost;
        // A wall is traversed ATOMICALLY within the step: when the exit lies
        // beyond
        // the endpoint the round is placed just past the exit anyway,
        // overshooting
        // by at most the wall's thickness — a rounding artefact at ~23 m a
        // step.
        const advance = exitT + EXIT_EPS;
        curX += dirX * advance;
        curY += dirY * advance;
        curZ += dirZ * advance;
        distance += advance;
        remaining -= advance;
        continue;
      }

      // Not enough pen left — or the per-step penetration budget is spent, in
      // which case the round stops here rather than getting a free pass.
      return { state: null, hit: null, wallStop: impact, wallMaterial: material, glass };
    }

    // 5. Nothing in the way: fly the rest of the segment.
    curX += dirX * remaining;
    curY += dirY * remaining;
    curZ += dirZ * remaining;
    distance += remaining;
    remaining = 0;
  }

  // Retirement checks run on the post-step state, so a round that expires this
  // tick still resolved any hit it made on the way.
  const horizSpeed = Math.sqrt(vx * vx + vz * vz);
  if (distance > MAX_PROJECTILE_RANGE || horizSpeed < MIN_PROJECTILE_SPEED) {
    return { state: null, hit: null, wallStop: null, wallMaterial: null, glass };
  }

  return {
    state: {
      pos: { x: curX, y: curY, z: curZ },
      vel: { x: vx, y: vy, z: vz },
      baseDamage,
      penPower,
      distance,
      shooterId: p.shooterId,
      falloffStartM: p.falloffStartM,
      falloffEndM: p.falloffEndM,
      falloffMinMult: p.falloffMinMult,
    },
    hit: null,
    wallStop: null,
    wallMaterial: null,
    glass,
  };
}

/**
 * Distance along the ray at which it LEAVES `box` (slab method, far plane).
 * Ignores any `maxDist`: the answer wanted is the material's true thickness.
 * `0` for a parallel outside ray — impossible after an entry hit, but total.
 */
function exitDistance(origin: Vec3, dir: Vec3, box: AABB): number {
  let tmax = Infinity;

  if (dir.x !== 0) {
    const inv = 1 / dir.x;
    const t1 = (box.min.x - origin.x) * inv;
    const t2 = (box.max.x - origin.x) * inv;
    const far = t1 > t2 ? t1 : t2;
    if (far < tmax) tmax = far;
  } else if (origin.x < box.min.x || origin.x > box.max.x) {
    return 0;
  }

  if (dir.y !== 0) {
    const inv = 1 / dir.y;
    const t1 = (box.min.y - origin.y) * inv;
    const t2 = (box.max.y - origin.y) * inv;
    const far = t1 > t2 ? t1 : t2;
    if (far < tmax) tmax = far;
  } else if (origin.y < box.min.y || origin.y > box.max.y) {
    return 0;
  }

  if (dir.z !== 0) {
    const inv = 1 / dir.z;
    const t1 = (box.min.z - origin.z) * inv;
    const t2 = (box.max.z - origin.z) * inv;
    const far = t1 > t2 ? t1 : t2;
    if (far < tmax) tmax = far;
  } else if (origin.z < box.min.z || origin.z > box.max.z) {
    return 0;
  }

  return tmax === Infinity || tmax < 0 ? 0 : tmax;
}
